import React, { useEffect, useRef, useState, ReactNode, CSSProperties } from 'react';
import { IconX } from '@tabler/icons-react';
import { Portal } from 'components/portal';
import Button from 'components/button/Button';
import { useTheme } from 'hooks/useTheme';
import styles from './Modal.css';

interface Props {
    visible: boolean;
    noCloseButton?: boolean;
    onClose: () => void;
    modalStyle?: CSSProperties;
    children: ReactNode;
}

const Modal = ({ visible, noCloseButton = false, onClose, modalStyle = {}, children }: Props) => {
    const theme = useTheme();
    const [renderModal, setRenderModal] = useState(false);
    const [animationState, setAnimationState] = useState<'initial' | 'entering' | 'entered' | 'exiting'>('initial');
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (visible) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            setRenderModal(true);
            setAnimationState('initial');
            
            timeoutRef.current = setTimeout(() => {
                setAnimationState('entering');
                timeoutRef.current = setTimeout(() => {
                    setAnimationState('entered');
                }, 150);
            }, 10);
        } else if (renderModal) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            setAnimationState('exiting');
            timeoutRef.current = setTimeout(() => {
                setRenderModal(false);
                setAnimationState('initial');
            }, 150);
        }
    }, [visible]);

    if (!renderModal) {
        return null;
    }

    const getModalClassName = () => {
        const base = styles.modalContainer;
        switch (animationState) {
            case 'initial': return `${base} ${styles.modalInitial}`;
            case 'entering': return `${base} ${styles.modalEntering}`;
            case 'entered': return `${base} ${styles.modalVisible}`;
            case 'exiting': return `${base} ${styles.modalExiting}`;
            default: return `${base} ${styles.modalInitial}`;
        }
    };

    const isBackdropVisible = animationState === 'entering' || animationState === 'entered';

    return (
        <Portal>
            <div 
                className={styles.overlay} 
                style={{ pointerEvents: visible ? 'auto' : 'none' }}
            >
                <div 
                    className={styles.backdropContainer}
                    onClick={onClose}
                >
                    <div 
                        className={`${styles.backdrop} ${isBackdropVisible ? styles.backdropVisible : ''}`}
                        style={{ backgroundColor: theme.colors.modalOverlay }}
                    />
                </div>

                <div
                    className={getModalClassName()}
                    style={{
                        backgroundColor: theme.colors.modalBackground,
                        ...modalStyle,
                    }}
                >
                    {!noCloseButton && (
                        <Button 
                            style={{ backgroundColor: theme.colors.surface }}
                            className={styles.closeButton}
                            onPress={onClose}
                            activeOpacity={0.8}
                        >
                            <IconX size={52} color={theme.colors.text} />
                        </Button>
                    )}
                    {children}
                </div>
            </div>
        </Portal>
    );
};

export default Modal;
